/**
 * Card-Centric Flow: status → UI mapping for OwnerDashboard
 * Each order status defines: label, icon, color, pulse, ownerAction, description, step
 */

export const CARD_FLOW_CONFIG = {
  PENDING_QUOTE: {
    label: 'Minting under review',
    icon: '⏳',
    color: 'yellow',
    pulse: false,
    ownerAction: null,
    description: 'The Lab is reviewing your submission and will assign a grader.',
    step: 1,
    totalSteps: 9,
  },

  QUOTE_SENT: {
    label: 'Quote Available',
    icon: '💰',
    color: 'cyan',
    pulse: true,
    ownerAction: 'PAY',
    description: 'Your quote has been sent. Review the price and complete payment.',
    step: 2,
    totalSteps: 9,
  },

  AWAITING_PAYMENT: {
    label: 'Awaiting Payment',
    icon: '💰',
    color: 'cyan',
    pulse: true,
    ownerAction: 'PAY',
    description: 'Complete payment to continue the process.',
    step: 2,
    totalSteps: 9,
  },

  CREATED: {
    label: 'Order Created',
    icon: '📋',
    color: 'gray',
    pulse: false,
    ownerAction: null,
    description: 'Order created. Awaiting price to be set.',
    step: 1,
    totalSteps: 9,
  },

  PRICE_SET: {
    label: 'Price Set',
    icon: '💰',
    color: 'cyan',
    pulse: true,
    ownerAction: 'PAY',
    description: 'Price has been set. Complete payment.',
    step: 2,
    totalSteps: 9,
  },

  PAYMENT_PENDING: {
    label: 'Awaiting Payment',
    icon: '💰',
    color: 'cyan',
    pulse: true,
    ownerAction: 'PAY',
    description: 'Complete payment to continue.',
    step: 2,
    totalSteps: 9,
  },

  PAID: {
    label: 'Prepaid — Lab intake',
    icon: '✅',
    color: 'green',
    pulse: false,
    ownerAction: null,
    description:
      'Your M1NT credit covers this submission. M1NT Lab will assign a grader and email shipping instructions—no extra payment in the app.',
    step: 3,
    totalSteps: 9,
  },

  AWAITING_SHIPMENT: {
    label: 'Ship to grader',
    icon: '📦',
    color: 'orange',
    pulse: true,
    ownerAction: 'CONFIRM_SHIPMENT',
    description: 'Follow the shipping instructions and send your card to the assigned grader.',
    // FlowTimeline: step 3 = Shipping (current). Using 4 wrongly marked Shipping ✓ and Received as active.
    step: 3,
    totalSteps: 9,
  },

  SHIPPED_BY_OWNER: {
    label: 'Shipped',
    icon: '🚚',
    color: 'blue',
    pulse: false,
    ownerAction: null,
    description: 'Card shipped! Waiting for the grader to confirm receipt.',
    // Step 4 = Received (current) while lab has not confirmed receipt yet.
    step: 4,
    totalSteps: 9,
  },

  CARD_RECEIVED: {
    label: 'Received by grader',
    icon: '📬',
    color: 'blue',
    pulse: false,
    ownerAction: null,
    description: 'The grader has received your card. Grading will begin soon.',
    step: 5,
    totalSteps: 9,
  },

  IN_PROGRESS: {
    label: 'In Grading',
    icon: '🔍',
    color: 'purple',
    pulse: false,
    ownerAction: null,
    description: 'Your card is being evaluated by a professional grader.',
    step: 6,
    totalSteps: 9,
  },

  COMPLETED: {
    label: 'Graded',
    icon: '⭐',
    color: 'gold',
    pulse: true,
    ownerAction: 'VIEW_GRADE',
    description: 'Grading complete! View your card grade.',
    step: 7,
    totalSteps: 9,
  },

  GRADED: {
    label: 'Graded',
    icon: '⭐',
    color: 'gold',
    pulse: true,
    ownerAction: 'VIEW_GRADE',
    description: 'Grading complete! View your card grade.',
    step: 7,
    totalSteps: 9,
  },

  NFT_MINTED: {
    label: 'NFT Minted',
    icon: '⚡',
    color: 'emerald',
    pulse: false,
    ownerAction: null,
    description: 'Once M1NT Lab ships your card we will notify you.',
    step: 8,
    totalSteps: 9,
  },

  MINTED: {
    label: 'NFT Minted',
    icon: '⚡',
    color: 'emerald',
    pulse: false,
    ownerAction: null,
    description: 'Once M1NT Lab ships your card we will notify you.',
    step: 8,
    totalSteps: 9,
  },

  SHIPPED_TO_OWNER: {
    label: 'On the Way',
    icon: '🚚',
    color: 'teal',
    pulse: true,
    ownerAction: 'CONFIRM_DELIVERY',
    description: 'Your card is on its way! Confirm when you receive it.',
    step: 9,
    totalSteps: 9,
  },

  DELIVERED: {
    label: 'Delivered ✓',
    icon: '🏆',
    color: 'premium',
    pulse: false,
    ownerAction: null,
    description: 'All done! Card and NFT are in your hands.',
    step: 9,
    totalSteps: 9,
  },

  CANCELLED: {
    label: 'Cancelled',
    icon: '❌',
    color: 'red',
    pulse: false,
    ownerAction: null,
    description: 'This order was cancelled.',
    step: 0,
    totalSteps: 0,
  },
}

export const OWNER_ACTIONS = {
  PAY: {
    label: 'Pay Now',
    buttonColor: 'bg-cyan-500 hover:bg-cyan-600',
    icon: '💳',
  },
  CONFIRM_SHIPMENT: {
    label: 'Confirm Shipment',
    buttonColor: 'bg-orange-500 hover:bg-orange-600',
    icon: '📦',
  },
  VIEW_GRADE: {
    label: 'View Grade',
    buttonColor: 'bg-yellow-500 hover:bg-yellow-600',
    icon: '⭐',
  },
  VIEW_NFT: {
    label: 'View NFT',
    buttonColor: 'bg-emerald-500 hover:bg-emerald-600',
    icon: '⚡',
  },
  CONFIRM_DELIVERY: {
    label: 'Confirm Receipt',
    buttonColor: 'bg-teal-500 hover:bg-teal-600',
    icon: '✅',
  },
}

export const TIMELINE_STEPS = [
  { key: 'quote', label: 'Quote', icon: '📋' },
  { key: 'payment', label: 'Payment', icon: '💰' },
  { key: 'shipping', label: 'Shipping', icon: '📦' },
  { key: 'received', label: 'Received', icon: '📬' },
  { key: 'grading', label: 'Grading', icon: '🔍' },
  { key: 'graded', label: 'Grade', icon: '⭐' },
  { key: 'minted', label: 'NFT', icon: '⚡' },
  { key: 'returned', label: 'Return', icon: '🚚' },
  { key: 'done', label: 'Done', icon: '🏆' },
]

/** Get config for order status; fallback for unknown */
export function getFlowConfig(status) {
  if (!status) return CARD_FLOW_CONFIG.PENDING_QUOTE
  return (
    CARD_FLOW_CONFIG[status] || {
      label: status,
      icon: '•',
      color: 'gray',
      pulse: false,
      ownerAction: null,
      description: '',
      step: 0,
      totalSteps: 9,
    }
  )
}
